import { PaymentMethod } from '@baemingo/report-events/dist/types';

import * as actions from '~/actions';
import { useDispatch, useI18n, useLogger, useSelector, useSKV } from '~Hooks';
import { ORDER_ORIGIN } from '~../__queries__';
import {
  GetPendingOrders_venue_orders_items_variations,
  GetPendingOrders_venue_orders as IncomingOrder,
} from '~Queries/__queries__';
import { useReportsApi } from '~Components/ReportsUniverse';
import { useInventoryApi, useInventoryUniverse } from '~Components/InventoryUniverse';
import { gql, useMutation } from '@apollo/client';
import { useStorage } from '~Components/Storage';

const UPDATE_ORDER = gql`
  mutation UpateOrder($id: ID!, $posDeviceId: String!, $complianceId: String!) {
    updateOrder(id: $id, posDeviceId: $posDeviceId, complianceId: $complianceId) {
      id
    }
  }
`;

export const useIncomingOrderUtility = () => {
  const [storage, updateStorage] = useStorage();
  const [updateOrder] = useMutation(UPDATE_ORDER);
  const dispatch = useDispatch();
  const reportsApi = useReportsApi();
  const syncSKV = useSKV();
  const { i18n } = useI18n();
  const [, , venueId] = location.hash.split('/');
  const inventoryUniverse = useInventoryUniverse();
  const inventoryApi = useInventoryApi(venueId);

  const logger = useLogger('incoming-orders-utility');
  const receiptCounter = useSelector((state) => state.POSReceiptCounter);

  const sendToReduxState = (order: IncomingOrder) => {
    const richVariations: {
      id: string; // The entryId
      variationId: string; // The variationId
      count: number; // How many of this variation is used
    }[] = order.richVariations;

    const usedIndexes: Record<number, boolean> = {};

    function repeatVariationsBasedOnCount(
      entryId: string,
      variations: GetPendingOrders_venue_orders_items_variations[],
    ) {
      return variations.reduce((acc, variation, currentIndex) => {
        const richVariation = richVariations.find(
          (rv, index) =>
            !usedIndexes[index] && rv.id === entryId && rv.variationId === variation.id,
        );

        if (richVariation && richVariation.count > 1) {
          // If there is more than one count, change the name to reflect that
          usedIndexes[currentIndex] = true; // Mark this index as used

          logger.info(`${richVariation.count}x ${variation.name}`, richVariation);

          return acc.concat({
            ...variation,
            name: `${richVariation.count}x ${variation.name}`,
          });
        } else {
          // If not used, just return the original variation
          return acc.concat(variation);
        }
      }, [] as GetPendingOrders_venue_orders_items_variations[]);
    }

    /**
     * We have our array of rich variations. Now we need to
     */

    dispatch({
      type: 'SEND_ONLINE_ORDER',
      order: {
        id: order.easyId,

        // We use this field to connect this order to the KDS
        // Skip this if we are using table orders
        realId: order.tableId ? null : order.id,

        chequeName: order.tableId ? order.easyId : null, // Ensures that we correctly display Table ${order.easyId} if the order is a table order
        tableName: order.user.name,

        orderNumber: order.easyId,
        comment: order.comment || order.thirdPartyMetadata?.client,
        estimatedFinishTime: order.estimatedFinishTime,
        desiredTime: order.desiredTime,
      },
      serviceType: order.type,
      tableId: order.tableId,
      origin: ORDER_ORIGIN.APP,
      items: order.items.map((item) => ({
        id: Math.random()
          .toString(36)
          .substring(2, 15), // Id is needed for the bong system, but since we might have multiple of the same item, we generate a random one
        price: item.price.amount,
        percentageVAT: item.price.percentageVAT,
        discount: 0,
        itemId: item.entry.item.id,
        categoryId: item.entry.item.categories[0].id,
        count: 1,
        fraction: 1,
        title: i18n(item.entry.item.titleLang),
        entryId: item.entry.id,
        variations: repeatVariationsBasedOnCount(
          item.entry.id,
          item.variations.filter((vari) => {
            return (
              !vari.name.startsWith('Inget ') &&
              !vari.name.startsWith('Ingen ') &&
              !vari.name.startsWith('Inga ') &&
              !vari.name.startsWith('No ')
            );
          }),
        ),
        removedVariations: item.removedVariations,
        courseType: item.entry.item.courseType || undefined,
      })),
    });
  };

  const syncWithSKV = (order: IncomingOrder) => {
    logger.info('Initiating SKV sync for Online Order with EasyID: ' + order.easyId, {
      order,
    });

    const amount = order.items.reduce(
      (acc, item) => acc + item.price.amount - item.price.discount,
      0,
    );

    const formattedItems = order.items.map((item) => ({
      id: item.id,
      amount: item.price.amount,
      discount: item.price.discount,
      vatRate: item.price.percentageVAT,
    }));

    const MAX_FAIL_COUNT = 3;
    let failCount = 0;

    const run = () => {
      const time = new Date().getTime();
      syncSKV(time, formattedItems, amount, receiptCounter + 1).then(
        ({ success, complianceId, controlUnitSerial, productionNumber, errorMessage }) => {
          if (success) {
            logger.info('Successful SKV sync for Online Order with EasyID: ' + order.easyId, {
              order,
            });

            updateStorage({
              taxComplianceControlUnitSerial: controlUnitSerial,
              taxComplianceProductionNumber: productionNumber,
            });

            dispatch({
              type: 'INCREASE_RECEIPT_COUNTER',
            });

            updateOrder({
              variables: {
                id: order.id,
                posDeviceId: storage.taxComplianceDeviceId,
                complianceId,
              },
            })
              .then(() => {
                logger.info('ComplianceID Updated', {
                  order,
                  complianceId,
                });
              })
              .catch((err) => {
                logger.error('Failed to update order with complianceId', {
                  order,
                  complianceId,
                  err,
                });
              });

            return;
          }

          if (failCount < MAX_FAIL_COUNT) {
            failCount = failCount + 1;

            logger.warn('Failed SKV sync for Online Order with EasyID: ' + order.easyId, {
              order,
              failCount,
              errMessage: errorMessage,
            });

            setTimeout(() => {
              run();
            }, 3000);
          } else {
            logger.error('Aborted SKV sync for Online Order with EasyID: ' + order.easyId, {
              order,
              failCount,
              errMessage: errorMessage,
            });
          }
        },
      );
    };

    run();
  };

  const addToZReport = (order: IncomingOrder) => {
    const items = order.items.map((item) => ({
      amount: item.price.amount,
      category: item.entry.item.categories[0].name,
      vatRate: item.price.percentageVAT,
      discount: item.price.discount,
    }));

    const paymentMethod: PaymentMethod = {
      name: 'ONLINE',
      amount: order.payment.amount,
    };

    try {
      logger.info('Adding Online order to Z-Report. EasyID: ' + order.easyId, { order });
      reportsApi.addOrder(order.id, items, [paymentMethod], order.payment.correctedTip ?? 0);
    } catch (err) {
      logger.error('Failed to add Online order to Z-Report. EasyID: ' + order.easyId, { order });
    }
  };

  const reduceStock = (itemId: string) => {
    const invItem = inventoryUniverse.items.find((i) => i.itemId === itemId);

    if (invItem && typeof invItem.count === 'number') {
      inventoryApi.reduceStock(invItem.itemId, 1, 'Order');
    }
  };

  const subtractOrderInventory = (order: IncomingOrder) => {
    // Loop through all items in the order and reduce inventory where applicable
    order.items.forEach((item) => {
      // Reduce stock for all variations that are marked as items
      const variationsAsItem = item.variations.filter((v) => v.asItem?.id);
      variationsAsItem.forEach((v) => reduceStock(v.asItem.id));

      // Reduce stock for the main item
      reduceStock(item.entry.item.id);
    });
  };

  return {
    sendToReduxState,
    syncWithSKV,
    addToZReport,
    subtractOrderInventory,
  };
};
